/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { SyncService } from '../../api/sync.service';
import { User } from '../../data/models/user';
import { CreateProjectDialogComponent } from '../../components/dialogs/create-project-dialog/create-project-dialog.component';
import { PaginationService } from 'src/app/data/pagination/pagination.service';
import { StringConstants } from '../../constants/string-constants';
import { Project } from '../../data/models/project';
import { ProjectType } from '../../data/models/project-type';
import { ProjectFilteredList } from '../../data/models/project-filtered-list';
import { ProjectService } from '../../data/project/project.service';
import { ProjectsApiService } from '../../api/projects-api.service';
import { ProjectStatus } from '../../data/models/project-status';
import { AdminService } from '../../data/admin/admin.service';
import { Subscription } from 'rxjs';
import { ProjectConfigurationService } from '../../data/project/project-configuration.service';
import { ModuleService } from '../../data/project/module.service';
import { UserService } from '../../data/user/user.service';
import { WelcomeMessagePopupComponent } from 'src/app/components/dialogs/welcome-message-popup/welcome-message-popup.component';
import { ProjectStatusEnum } from 'src/app/constants/project-status';
import { MatTabGroup } from '@angular/material/tabs';
import { FilterTypes } from '../../constants/filter-types';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild('listTabGroup') listTabGroup: MatTabGroup;
  dashboardTitle: string;
  dashboardSubtitle: string;
  pageInfo = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec vel ante ac risus.';
  columnsToDisplay = [
    { label: 'Project Id', value: 'projectId' },
    { label: 'Workspace', value: 'fabricWorkspaceName' },
    { label: 'Project Name', value: 'projectTitle' },
    { label: 'Project Type', value: 'projectType' },
    { label: 'Solution Offering', value: 'solutionOffering' },
    { label: 'Service Line', value: 'serviceLine' },
    { label: 'Industry', value: 'industry' },
    { label: 'Project Team', value: 'projectTeam' },
    { label: 'Status', value: 'status' },
  ];
  users: User[] = [];
  projectListViewDetails: Project[] = [];
  recentlyWorkedProjectList: Project[] = [];
  needAttentionProjectList: Project[] = [];
  otherProjectsList: Project[] = [];
  projectsTileViewDetails: Project[] = [];
  totalProjectsCount: number;
  defaultPageSize: number;
  pageSizeOptions: number[] = [];
  currentPageNumber: number;
  isDataLoaded: boolean;
  projectType: ProjectType[];
  projectStatus: ProjectStatus[];
  defaultOffset: number;
  subscriptions: Subscription[] = [];
  userPreference: boolean = this.userService.userPreference;
  projectList: Project[] = [];
  selectedSort: string;

  tabName = 'needattention';
  clickedTab = 'attention';
  totalRecords: number;
  isLoginUserProjectAdmin: boolean;
  totalSelectedFilterCount = 0;
  sortProjectsMenuValue: ProjectStatus[] = [];
  selectedTileViewSortValue: ProjectStatus;
  selectedListViewSortValue: ProjectStatus;

  constructor(
    private syncService: SyncService,
    public dialog: MatDialog,
    private projectApiService: ProjectsApiService,
    private projectDataService: ProjectService,
    private cdr: ChangeDetectorRef,
    private paginationService: PaginationService,
    public adminService: AdminService,
    public pcDataService: ProjectConfigurationService,
    private moduleService: ModuleService,
    public userService: UserService
  ) {
    this.dashboardTitle = StringConstants.DASHBOARD;
    this.dashboardSubtitle = StringConstants.DASHBOARDDESCRIPTION;
    this.isDataLoaded = false;
    this.totalProjectsCount = 0;
    this.projectDataService.filterData = { sortBy: '', sortOrder: 1 };
  }

  ngOnInit(): void {
    this.sortProjectsMenuValue = this.projectDataService.sortProjectsMenuValue;
    // clearing project config form group data on exit configuration
    this.clearProjectConfigurationData();
    this.defaultPageSize = this.paginationService.defaultPageSize;
    this.pageSizeOptions = this.paginationService.pageSizeOptions;
    this.paginationSubscriptions();

    const navigationId = history.state.navigationId;
    //if userPreference is set to false & user is landing on dashboard page for the first time, open the Welcome dialog box
    if (!this.userPreference && navigationId <= 2) {
      this.dialog.open(WelcomeMessagePopupComponent, {
        data: this.userPreference,
        disableClose: true,
      });
    }

    this.syncService.refreshProjectList.asObservable().subscribe(() => {
      this.loadProjectList();
    });

    this.subscriptions.push(
      this.projectDataService.tileViewSortValue.subscribe((value: ProjectStatus) => {
        this.updateTileViewSortValue(value);
        this.selectedTileViewSortValue = value;
      })
    );

    this.subscriptions.push(
      this.projectDataService.listViewSortValue.subscribe((value: ProjectStatus) => {
        /*        this.getListViewSortValue(value);*/
        this.selectedListViewSortValue = value;
      })
    );
  }

  clearProjectConfigurationData(): void {
    this.pcDataService.projectConfigurationFormGroup.reset();
    this.pcDataService.createForm();
    this.moduleService.clearServiceData();
  }

  paginationSubscriptions(): void {
    //updating the range label when page is changed
    this.subscriptions.push(
      this.syncService.updateRangeLabelNotifier.asObservable().subscribe((pageNumber) => {
        this.paginationService.customizePageRangeLabel(this.paginator, this.totalProjectsCount, pageNumber);
      })
    );

    this.subscriptions.push(
      this.syncService.refreshProjectList.asObservable().subscribe(() => {
        this.loadProjectList();
      })
    );

    this.projectDataService.shouldReloadProjects.subscribe((should) => {
      if (should) {
        this.loadProjectList();
      }
    });
  }

  loadProjectList(): void {
    //api call to load the first page data for the project list view
    this.subscriptions.push(
      this.projectApiService.getProjectList(this.projectDataService.filterData).subscribe(
        (projectDetails: ProjectFilteredList) => {
          //assigning the project type list
          this.projectType = projectDetails.projectType;
          this.projectStatus = projectDetails.projectStatus;

          //assigning the total project count for paginator label purpose
          this.totalProjectsCount =
            this.clickedTab === 'all'
              ? projectDetails.otherProjectTotalRecords
              : this.clickedTab === 'attention'
              ? projectDetails.needAttentionTotalRecords
              : projectDetails.recentlyWorkedProjects.length;
          this.totalRecords = projectDetails.totalRecords;
          this.isLoginUserProjectAdmin = projectDetails.isLoginUserProjectAdmin;
          if (!this.userService.userHasPlatformAdminRole) {
            this.projectList = projectDetails.projectFilterList.filter(
              (obj) => obj.status !== ProjectStatusEnum.CANCELLED.toString()
            );
            //Bucket/Tabs project data
            this.recentlyWorkedProjectList = projectDetails.recentlyWorkedProjects.filter(
              (obj) => obj.status !== ProjectStatusEnum.CANCELLED.toString()
            );
            this.needAttentionProjectList = projectDetails.projectsNeedAttention.filter(
              (obj) => obj.status !== ProjectStatusEnum.CANCELLED.toString()
            );

            this.otherProjectsList = projectDetails.otherProjects.filter(
              (obj) => obj.status !== ProjectStatusEnum.CANCELLED.toString()
            );
          } else {
            this.projectList = projectDetails.projectFilterList.filter((obj) => obj);
            //Bucket/Tabs project data
            this.recentlyWorkedProjectList = projectDetails.recentlyWorkedProjects.filter((obj) => obj);
            this.needAttentionProjectList = projectDetails.projectsNeedAttention.filter((obj) => obj);
            this.otherProjectsList = projectDetails.otherProjects.filter((obj) => obj);
          }

          //setting the project type value for all projects
          this.projectDataService.setProjectType(this.projectList, this.projectType);

          this.projectsTileViewDetails = this.projectList;

          //assigning the project list for the grid first page
          this.projectListViewDetails = this.projectList.slice(this.defaultOffset, this.defaultPageSize);

          this.syncService.refreshProjectListDatasource.next(projectDetails);
          this.paginationService.updateRecordCount(this.totalProjectsCount);
          this.paginationService.customizePageRangeLabel(this.paginator, this.totalProjectsCount, 0);
          this.cdr.detectChanges();
          this.isDataLoaded = true;
        },
        (error) => {
          // resetting
          this.isDataLoaded = true;
        }
      )
    );
  }
  openCreateProjectDialog(): void {
    this.dialog.open(CreateProjectDialogComponent, {
      disableClose: true,
      minHeight: '100vh',
      height: '100%',
      width: '43.75rem',
      position: { right: '0', top: '0' },
      panelClass: ['sidebar-dialog', 'animate__animated', 'animate__slideInRight', 'animate__faster'],
    });
  }

  handlePageEvent(e: PageEvent): void {
    this.syncService.pageSizeChangeNotifier.next(e.pageSize);
  }

  filterDataDialog(): void {
    //specify the columns type for each filter
    const dataModel = {
      columns: [
        {
          columnName: 'projectName',
          columnDisplayName: 'Project Name',
          columnType: FilterTypes.STRING,
        },
        {
          columnName: 'projectType',
          columnDisplayName: 'Project Type',
          columnType: FilterTypes.LISTDATA,
          columnData: this.projectType,
        },
        {
          columnName: 'solutionOffering',
          columnDisplayName: 'Solution Offering',
          columnType: FilterTypes.STRING,
        },
        {
          columnName: 'serviceLine',
          columnDisplayName: 'Service Line',
          columnType: FilterTypes.STRING,
        },
        {
          columnName: 'industry',
          columnDisplayName: 'Industry',
          columnType: FilterTypes.STRING,
        },
        {
          columnName: 'projectTeam',
          columnDisplayName: 'Project Team',
          columnType: FilterTypes.STRING,
        },
        {
          columnName: 'status',
          columnDisplayName: 'Status',
          columnType: FilterTypes.LISTDATA,
          columnData: this.projectStatus,
        },
      ],
    };

    const dialogRef = this.adminService.openFilterDialog(
      dataModel,
      this.projectDataService.filterData,
      this.adminService.appliedFilterData
    );
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((result) => {
        if (result.filterData) {
          this.totalSelectedFilterCount = result.customFilterData.length;
          const existingSortOrder = this.projectDataService.filterData['sortOrder'];
          const existingSortBy = this.projectDataService.filterData['sortBy'];
          this.adminService.appliedFilterData = result.customFilterData;
          this.projectDataService.filterData = result.filterData;
          this.projectDataService.filterData['sortBy'] = existingSortBy;
          this.projectDataService.filterData['sortOrder'] = existingSortOrder;

          if (result.eventClick === 'btnApplyClicked') {
            this.isDataLoaded = false;
            this.loadProjectList();
          }
        } else {
          this.projectDataService.filterData = { sortBy: '', sortOrder: 1 };
        }
      })
    );
  }

  /*Select tabs on changed function*/
  onTabChanged(event: any): void {
    this.clickedTab = event.tab.textLabel;
    this.tabName = this.clickedTab === 'all' ? 'other' : this.clickedTab === 'attention' ? 'needattention' : 'recent';
    this.loadProjectList();
  }

  onTileIconTabChange(event: any): void {
    if (event.index === 1) {
      let selectedIndex = 0;
      if (!(this.userService.userHasPlatformAdminRole || this.isLoginUserProjectAdmin)) {
        this.tabName = 'recent';
        this.clickedTab = 'recent';
        selectedIndex = this.clickedTab === 'recent' ? 0 : 1;
      } else selectedIndex = this.clickedTab === 'attention' ? 0 : this.clickedTab === 'recent' ? 1 : 2;
      this.listTabGroup.selectedIndex = selectedIndex;
      this.getListViewSortValue(this.selectedListViewSortValue);
    } else {
      this.updateTileViewSortValue(this.selectedTileViewSortValue);
    }
  }

  updateTileViewSortValue(value: ProjectStatus): void {
    this.updateFilterObject(value);
    this.loadProjectList();
  }

  updateListViewSortValue(value: ProjectStatus): void {
    this.selectedSort = value.name;
    this.projectDataService.updateListViewSortValue(value);
    this.getListViewSortValue(value);
  }

  getListViewSortValue(value: ProjectStatus): void {
    this.updateFilterObject(value);
    this.loadProjectList();
  }

  updateFilterObject(selectedSortValue: ProjectStatus): void {
    switch (selectedSortValue.id) {
      case 1:
        this.projectDataService.filterData.sortBy = 'projectName';
        this.projectDataService.filterData.sortOrder = 0;
        break;
      case 2:
        this.projectDataService.filterData.sortBy = 'projectName';
        this.projectDataService.filterData.sortOrder = 1;
        break;
      case 3:
        this.projectDataService.filterData.sortBy = 'createdOn';
        this.projectDataService.filterData.sortOrder = 1;
        break;
      case 4:
        this.projectDataService.filterData.sortBy = 'updatedOn';
        this.projectDataService.filterData.sortOrder = 1;
        break;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.adminService.appliedFilterData = [];
  }

  // // Check and set fabric job statuses for every project
  // setFabricJobStatuses(projects: Project[]): void {
  //   projects.forEach((project) => {
  //     //this.checkFabricExecutionStatus(project);
  //   });
  // }

  // checkFabricExecutionStatus(project: Project): void {
  //   this.subscriptions.push(
  //     this.projectApiService.checkFabricExecutionStatus(project.projectId).subscribe({
  //       next: (response) => {
  //         project.doesFabricJobExist = response;
  //       }
  //     })
  //   );
  // }
}
